import React, { useState, useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import TopBar from "../../sections/TopBar";
import Sidebar from "../../sections/Sidebar";
import "../../customCss/ManageOfficers.css";
import DataService from "../../services/data.service";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import NotFound from "../../images/no-data-found.jpeg";
import Spinner from "../../sections/Spinner";
import { TextField, Autocomplete } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const plans = [
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Bi-Weekly", value: "bi-weekly" },
  { label: "Weekly", value: "weekly" },
  { label: "Semi-Annual", value: "semi-annual" },
  { label: "2 Year", value: "2 year" },
];

const paymentModes = [
  { label: "Autopay", value: "Autopay" },
  { label: "Manual", value: "Manual" },
];

const Subscriptions = () => {
  const ref = useRef();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState({});
  const [deleteSubscriptionPopup, setDeleteSubscriptionPopup] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addNewPopup, setAddNewPopup] = useState(false);

  const [subscriptionName, setSubscriptionName] = useState("");
  const [subscriptionEmail, setSubscriptionEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [planDuration, setPlanDuration] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [cost, setCost] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);

  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = "Manage Subscriptions || Admin Panel";
    window.scrollTo(0, 0);
    getAllSubscriptions();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setIsSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const getAllSubscriptions = () => {
    ref.current.continuousStart();
    setLoading(true);
    DataService.getSubscriptions()
      .then((res) => {
        setAllSubscriptions(res?.data?.data || []);
        ref.current.complete();
        setLoading(false);
      })
      .catch((error) => {
        toast.error("Failed to fetch Subscription Details");
        ref.current.complete();
        setLoading(false);
      });
  };

  const resetFields = () => {
    setAddNewPopup(false);
    setSubscriptionName("");
    setPlanDuration(null);
    setExpiryDate(null);
    setStartDate(null);
    setCost("");
    setContactName("");
    setContactPhone("");
    setContactEmail("");
    setSubscriptionEmail("");
    setPassword("");
    setAccountNumber("");
    setSelectedSubscription({});
    setPaymentMode(null);
    setEditing(false);
  };

  const setData = (item) => {
    setSelectedSubscription(item);
    setEditing(true);
    setSubscriptionName(item?.subscriptionName || "");
    setPlanDuration(
      plans?.find((p) => {
        const val = Array.isArray(item?.planDuration) ? item?.planDuration[0] : item?.planDuration;
        return p?.value?.toLowerCase() === val?.toLowerCase();
      }) || null
    );
    setStartDate(item?.startDate ? dayjs(item?.startDate) : null);
    setExpiryDate(item?.expiryDate ? dayjs(item?.expiryDate) : null);
    setCost(item?.cost || "");
    setSubscriptionEmail(item?.subscriptionEmailId || "");
    setPassword(item?.password || "");
    setAccountNumber(item?.accountNumber || "");
    setContactName(item?.name || "");
    setContactPhone(item?.phoneNo || "");
    setContactEmail(item?.email || "");
    setPaymentMode(
      paymentModes?.find((p) => p.value === item?.paymentMode) || null
    );
    setAddNewPopup(true);
  };

  const handleDeleteSubscription = () => {
    setBtnLoading(true);
    DataService.deleteSubscription(selectedSubscription._id).then(
      () => {
        toast.success(`Subscription details deleted successfully`);
        resetFields();
        getAllSubscriptions();
        setDeleteSubscriptionPopup(false);
        setBtnLoading(false);
      },
      (error) => {
        setBtnLoading(false);
        toast.error(error?.response?.data?.message || "Delete failed");
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    const data = {
      ...(planDuration?.value && { planDuration: [planDuration?.value] }),
      ...(expiryDate && { expiryDate }),
      ...(paymentMode?.value && { paymentMode: paymentMode?.value }),
      ...(subscriptionName && { subscriptionName }),
      ...(startDate && { startDate }),
      ...(cost && { cost: parseFloat(cost) }),
      ...(subscriptionEmail && { subscriptionEmailId: subscriptionEmail }),
      ...(password && { password }),
      ...(accountNumber && { accountNumber }),
      ...(contactName && { name: contactName }),
      ...(contactEmail && { email: contactEmail }),
      ...(contactPhone && { phoneNo: contactPhone }),
    };

    DataService.addSubscription(data)
      .then(() => {
        getAllSubscriptions();
        resetFields();
        toast.success("Subscription details added successfully");
        setBtnLoading(false);
      })
      .catch((error) => {
        setBtnLoading(false);
        toast.error(error?.response?.data?.message || "Add failed");
      });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    const data = {
      ...(planDuration?.value && { planDuration: [planDuration?.value] }),
      ...(expiryDate && { expiryDate }),
      ...(paymentMode?.value && { paymentMode: paymentMode?.value }),
      ...(subscriptionName && { subscriptionName }),
      ...(startDate && { startDate }),
      ...(cost && { cost: parseFloat(cost) }),
      ...(subscriptionEmail && { subscriptionEmailId: subscriptionEmail }),
      ...(password && { password }),
      ...(accountNumber && { accountNumber }),
      ...(contactName && { name: contactName }),
      ...(contactEmail && { email: contactEmail }),
      ...(contactPhone && { phoneNo: contactPhone }),
    };

    DataService.updateSubscription(selectedSubscription?._id, data)
      .then(() => {
        getAllSubscriptions();
        resetFields();
        toast.success("Subscription details updated successfully");
        setBtnLoading(false);
      })
      .catch((error) => {
        setBtnLoading(false);
        toast.error(error?.response?.data?.message || "Update failed");
      });
  };

  const exportToExcel = () => {
    if (!allSubscriptions || allSubscriptions.length === 0) {
      toast.error("No Data Available to Print");
      return;
    }
    setLoading(true);
    const formattedData = allSubscriptions.map((item, index) => ({
      "S. No.": index + 1,
      "Subscription Name": item?.subscriptionName || "-",
      "Subscription Email": item?.subscriptionEmailId || "-",
      "Password": item?.password || "-",
      "Account No.": item?.accountNumber || "-",
      "Plan Duration": item?.planDuration || "-",
      "Payment Mode": item?.paymentMode || "-",
      "Start Date": item?.startDate ? moment(item?.startDate).format("MM/DD/YYYY") : "-",
      "Expiry Date": item?.expiryDate ? moment(item?.expiryDate).format("MM/DD/YYYY") : "-",
      "Cost": item?.cost ? `$ ${item?.cost}` : "-",
      "Contact Person": item?.name || "-",
      "Contact Email": item?.email || "-",
      "Contact Phone": item?.phoneNo || "-",
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subscriptions");
    XLSX.writeFile(workbook, "All_Subscriptions.xlsx");
    setLoading(false);
  };

  return (
    <>
      <LoadingBar color="#084e8a" ref={ref} height={5} />
      <div className={`main_layout ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
        {addNewPopup && (
          <div className="popupOuter">
            <div className="popupInner">
              <i className="fas fa-times" onClick={resetFields} />
              <div className="popupHeader">
                <h3>{editing ? "Edit" : "Add New"} Subscription Detail</h3>
              </div>
              <div className="popupBody py-4">
                <div className="mb-4">
                  <TextField label="Subscription Name" variant="outlined" fullWidth size="small" value={subscriptionName} onChange={(e) => setSubscriptionName(e.target.value)} />
                </div>
                <div className="mb-4">
                  <Autocomplete
                    options={plans}
                    getOptionLabel={(option) => option?.label}
                    value={planDuration}
                    size="small"
                    sx={{ width: "100%", background: "#fff" }}
                    onChange={(event, newValue) => setPlanDuration(newValue)}
                    renderInput={(params) => <TextField {...params} label="Select Plan Duration" variant="outlined" />}
                  />
                </div>
                <div className="mb-4">
                  <Autocomplete
                    options={paymentModes}
                    getOptionLabel={(option) => option?.label}
                    value={paymentMode}
                    size="small"
                    sx={{ width: "100%", background: "#fff" }}
                    onChange={(event, newValue) => setPaymentMode(newValue)}
                    renderInput={(params) => <TextField {...params} label="Payment Mode" variant="outlined" />}
                  />
                </div>
                <div className="row mb-4">
                  <div className="col-sm-6">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker label="Start Date" sx={{ width: "100%" }} value={startDate} onChange={(val) => setStartDate(val)} slotProps={{ textField: { fullWidth: true, size: "small" } }} />
                    </LocalizationProvider>
                  </div>
                  <div className="col-sm-6">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker label="Expiry Date" disablePast sx={{ width: "100%" }} value={expiryDate} onChange={(val) => setExpiryDate(val)} slotProps={{ textField: { fullWidth: true, size: "small" } }} />
                    </LocalizationProvider>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-sm-6">
                    <TextField label="Cost" variant="outlined" fullWidth type="number" size="small" value={cost} onChange={(e) => setCost(e.target.value)} />
                  </div>
                  <div className="col-sm-6">
                    <TextField label="Subscription Email ID" variant="outlined" fullWidth size="small" value={subscriptionEmail} onChange={(e) => setSubscriptionEmail(e.target.value)} />
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-sm-6" style={{ position: "relative" }}>
                    <TextField label="Password" variant="outlined" fullWidth size="small" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                    <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 25, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#555" }} />
                  </div>
                  <div className="col-sm-6">
                    <TextField label="Account No." variant="outlined" fullWidth size="small" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                  </div>
                </div>
                <h5 className="mb-3">Contact Person Details</h5>
                <div className="row">
                  <div className="col-sm-4">
                    <TextField label="Name" variant="outlined" fullWidth size="small" value={contactName} onChange={(e) => setContactName(e.target.value)} />
                  </div>
                  <div className="col-sm-4">
                    <TextField label="Email" variant="outlined" fullWidth size="small" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                  </div>
                  <div className="col-sm-4">
                    <TextField label="Phone" variant="outlined" fullWidth size="small" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="popupFooter">
                <button className="btn btn-primary" onClick={editing ? handleUpdate : handleSubmit} disabled={btnLoading}>
                  {btnLoading ? <Spinner /> : editing ? "Update" : "Submit"}
                </button>
                <button className="btn btn-danger" onClick={resetFields}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        <Sidebar isSidebarOpen={isSidebarOpen} />
        <div className="innerRight">
          <TopBar title="Manage Subscriptions" menu="" toggleSidebar={toggleSidebar} />
          <div className="officersSec">
            <div className="row align-items-center mb-3">
              <div className="col-sm-6 col-6">
                <h3 className="colorHeadThree mb-0">Subscription Details</h3>
              </div>
              <div className="col-sm-6 col-6 text-right">
                <button className="btn btn-primary" onClick={() => setAddNewPopup(true)}>
                  <i className="fas fa-plus-circle me-2" /> Add New
                </button>
                <button className="btn btn-primary ms-3" onClick={exportToExcel} disabled={loading}>Download Sheet <i className="far fa-file-excel ms-2" /></button>
              </div>
            </div>
            {allSubscriptions?.length > 0 ? (
              <div className="tableScroller">
                <table className="table table-striped">
                  <thead style={{ whiteSpace: "nowrap" }}>
                    <tr>
                      <th scope="col">Action</th>
                      <th scope="col">Subscription Name</th>
                      <th scope="col">Email ID</th>
                      <th scope="col">Password</th>
                      <th scope="col">Account No.</th>
                      <th scope="col">Plan Duration</th>
                      <th scope="col">Payment Mode</th>
                      <th scope="col">Start Date</th>
                      <th scope="col">Expiry Date</th>
                      <th scope="col">Cost</th>
                      <th scope="col">Contact Person</th>
                    </tr>
                  </thead>
                  <tbody style={{ whiteSpace: "nowrap" }}>
                    {allSubscriptions.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <div className="tIco">
                            <i className="fas fa-pencil-alt me-2" onClick={() => setData(item)} />
                            <i className="fas fa-trash-alt me-2" onClick={() => { setSelectedSubscription(item); setDeleteSubscriptionPopup(true); }} />
                          </div>
                        </td>
                        <td>{item?.subscriptionName}</td>
                        <td>{item?.subscriptionEmailId || "-"}</td>
                        <td>{item?.password || "-"}</td>
                        <td>{item?.accountNumber || "-"}</td>
                        <td className="text-capitalize">{item?.planDuration}</td>
                        <td>{item?.paymentMode || "-"}</td>
                        <td>{item?.startDate ? moment(item?.startDate).format("MM/DD/YYYY") : "-"}</td>
                        <td>{item?.expiryDate ? moment(item?.expiryDate).format("MM/DD/YYYY") : "-"}</td>
                        <td>$ {item?.cost}</td>
                        <td>{item?.name || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !loading && (
                <div className="text-center mt-4">
                  <img src={NotFound} alt="noData" className="noData mb-2" />
                  <p className="m-0">No Subscription Records Found.</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <Modal show={deleteSubscriptionPopup} onHide={() => setDeleteSubscriptionPopup(false)} centered>
        <Modal.Header closeButton><Modal.Title>{selectedSubscription?.subscriptionName}</Modal.Title></Modal.Header>
        <Modal.Body><div className="text-center"><p className="mb-0">Are you sure you want to DELETE this subscription detail?</p></div></Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary" onClick={handleDeleteSubscription} disabled={btnLoading}>{btnLoading ? <Spinner /> : "Delete"}</button>
          <button type="button" className="btn btn-danger" onClick={() => setDeleteSubscriptionPopup(false)}>Cancel</button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Subscriptions;